import { Notification } from '@apis/index'
import { RootState } from '@store/index'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'

import { FiChevronLeft, FiChevronRight, FiCheck } from 'react-icons/fi'
import {
  Container,
  EmptyState,
  Header,
  Item,
  ItemBody,
  ItemDate,
  ItemFooter,
  ItemHeader,
  ItemMeta,
  ItemTime,
  ItemTitle,
  List,
  LoadingState,
  MarkReadButton,
  MetaItem,
  PageInfo,
  Pagination,
  PaginationButton,
  Summary,
  Title,
  UnreadBadge,
} from './style'

const NotificationList = () => {
  const {
    items: notifications,
    totalItems,
    totalPages,
  } = useSelector((state: RootState) => state.notifications)

  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)

  const fetchNotifications = async () => {
    try {
      setLoading(true)
      await Notification.list({
        state: 'notifications',
        params: { page, limit: 20 },
      })
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNotifications()
  }, [page])

  const handleMarkAsRead = async (id: number) => {
    try {
      await Notification.markAsRead({ id, state: 'notifications' })
      fetchNotifications()
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  return (
    <Container>
      <Header>
        <Title>Notifications</Title>
        <Summary>
          Showing {(page - 1) * 10 + 1}-{Math.min(page * 10, totalItems)} of{' '}
          {totalItems}
        </Summary>
      </Header>

      {loading ? (
        <LoadingState>Loading notifications...</LoadingState>
      ) : notifications.length === 0 ? (
        <EmptyState>No notifications found</EmptyState>
      ) : (
        <>
          <List>
            {notifications.map((notification) => (
              <Item key={notification.id} unread={!notification.isRead}>
                <ItemHeader>
                  <ItemTitle>{notification.title}</ItemTitle>
                  <ItemTime>
                    {dayjs(notification.createdAt).format('HH:mm')}
                    {!notification.read && <UnreadBadge />}
                  </ItemTime>
                </ItemHeader>
                <ItemBody>{notification.body}</ItemBody>
                {notification.infos && (
                  <ItemMeta>
                    <MetaItem>
                      <span>ID:</span> {notification.infos.id}
                    </MetaItem>
                    <MetaItem>
                      <span>Status:</span> {notification.infos.status}
                    </MetaItem>
                    <MetaItem>
                      <span>Type:</span> {notification.type}
                    </MetaItem>
                  </ItemMeta>
                )}
                <ItemFooter>
                  <ItemDate>
                    {dayjs(notification.createdAt).format('MMM D, YYYY')}
                  </ItemDate>
                  {!notification.isRead && (
                    <MarkReadButton
                      onClick={() => handleMarkAsRead(notification.id)}
                    >
                      <FiCheck size={14} /> Mark as read
                    </MarkReadButton>
                  )}
                </ItemFooter>
              </Item>
            ))}
          </List>

          {totalPages > 1 && (
            <Pagination>
              <PaginationButton
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                <FiChevronLeft />
              </PaginationButton>
              <PageInfo>
                Page {page} of {totalPages}
              </PageInfo>
              <PaginationButton
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                <FiChevronRight />
              </PaginationButton>
            </Pagination>
          )}
        </>
      )}
    </Container>
  )
}

export default NotificationList
